import { useState } from 'react';
import { useMarketingData } from '../MarketingContext';

export default function AnalyticsPage() {
  const { companies, analytics, campaigns } = useMarketingData();
  const [filterCompany, setFilterCompany] = useState('all');
  const [period, setPeriod] = useState('2026-08');

  const filtered = analytics.filter(a => {
    if (filterCompany !== 'all' && a.companyId !== filterCompany) return false;
    if (period !== 'all' && a.date !== period) return false;
    return true;
  });

  const totalImpressions = filtered.reduce((s, a) => s + a.impressions, 0);
  const totalClicks = filtered.reduce((s, a) => s + a.clicks, 0);
  const totalLeads = filtered.reduce((s, a) => s + a.leads, 0);
  const totalConversions = filtered.reduce((s, a) => s + a.conversions, 0);
  const totalRevenue = filtered.reduce((s, a) => s + a.revenue, 0);
  const totalSpend = filtered.reduce((s, a) => s + a.spend, 0);
  const ctr = totalImpressions > 0 ? ((totalClicks / totalImpressions) * 100).toFixed(1) : '0';
  const conversionRate = totalLeads > 0 ? ((totalConversions / totalLeads) * 100).toFixed(1) : '0';
  const cpl = totalLeads > 0 ? Math.round(totalSpend / totalLeads) : 0;
  const roas = totalSpend > 0 ? (totalRevenue / totalSpend).toFixed(1) : '0';

  const channelSummary = ['seo', 'ppc', 'social_media', 'content', 'email', 'direct'].map(ch => {
    const chData = filtered.filter(a => a.channel === ch);
    return {
      channel: ch,
      impressions: chData.reduce((s, a) => s + a.impressions, 0),
      clicks: chData.reduce((s, a) => s + a.clicks, 0),
      leads: chData.reduce((s, a) => s + a.leads, 0),
      conversions: chData.reduce((s, a) => s + a.conversions, 0),
      revenue: chData.reduce((s, a) => s + a.revenue, 0),
      spend: chData.reduce((s, a) => s + a.spend, 0),
    };
  }).filter(c => c.impressions > 0 || c.leads > 0);

  const companySummary = companies.map(co => {
    const coData = filtered.filter(a => a.companyId === co.id);
    return {
      company: co.name,
      leads: coData.reduce((s, a) => s + a.leads, 0),
      revenue: coData.reduce((s, a) => s + a.revenue, 0),
      spend: coData.reduce((s, a) => s + a.spend, 0),
    };
  }).filter(c => c.leads > 0 || c.revenue > 0);

  const kpiCards = [
    { label: 'Impressions', value: totalImpressions.toLocaleString(), icon: '👁️', color: 'bg-blue-500' },
    { label: 'Clicks', value: totalClicks.toLocaleString(), icon: '🖱️', color: 'bg-indigo-500' },
    { label: 'CTR', value: `${ctr}%`, icon: '📊', color: 'bg-violet-500' },
    { label: 'Leads', value: totalLeads.toLocaleString(), icon: '🎯', color: 'bg-emerald-500' },
    { label: 'Conversions', value: totalConversions.toLocaleString(), icon: '✅', color: 'bg-green-500' },
    { label: 'Conv. Rate', value: `${conversionRate}%`, icon: '📈', color: 'bg-teal-500' },
    { label: 'Revenue', value: `₹${(totalRevenue / 1000).toFixed(0)}K`, icon: '💰', color: 'bg-amber-500' },
    { label: 'Spend', value: `₹${(totalSpend / 1000).toFixed(0)}K`, icon: '💳', color: 'bg-rose-500' },
    { label: 'CPL', value: `₹${cpl}`, icon: '📉', color: 'bg-purple-500' },
    { label: 'ROAS', value: `${roas}x`, icon: '🚀', color: 'bg-cyan-500' },
  ];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div><h1 className="text-2xl font-bold text-gray-900">Analytics & Reports</h1><p className="text-sm text-gray-500">Marketing performance across all channels and companies</p></div>
      </div>

      <div className="flex gap-3 bg-white p-3 rounded-xl border border-gray-200">
        <select value={filterCompany} onChange={e => setFilterCompany(e.target.value)} className="text-sm border border-gray-200 rounded-lg px-3 py-1.5">
          <option value="all">All Companies</option>{companies.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
        <select value={period} onChange={e => setPeriod(e.target.value)} className="text-sm border border-gray-200 rounded-lg px-3 py-1.5">
          <option value="all">All Periods</option><option value="2026-08">August 2026</option><option value="2026-07">July 2026</option><option value="2026-06">June 2026</option>
        </select>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {kpiCards.map((kpi, i) => (
          <div key={i} className="bg-white border border-gray-200 rounded-xl p-4">
            <div className="flex items-center gap-2 mb-1">
              <div className={`h-6 w-6 rounded ${kpi.color} flex items-center justify-center text-white text-xs`}>{kpi.icon}</div>
              <span className="text-xs text-gray-500 font-medium">{kpi.label}</span>
            </div>
            <div className="text-xl font-bold text-gray-900">{kpi.value}</div>
          </div>
        ))}
      </div>

      {/* Channel Performance */}
      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
        <div className="px-5 py-3 bg-gray-50 border-b border-gray-200"><h3 className="text-sm font-bold text-gray-900">Channel Performance</h3></div>
        <table className="w-full text-sm">
          <thead><tr className="border-b border-gray-100">
            <th className="text-left px-5 py-2.5 text-xs font-semibold text-gray-600">Channel</th>
            <th className="text-right px-5 py-2.5 text-xs font-semibold text-gray-600">Impressions</th>
            <th className="text-right px-5 py-2.5 text-xs font-semibold text-gray-600">Clicks</th>
            <th className="text-right px-5 py-2.5 text-xs font-semibold text-gray-600">Leads</th>
            <th className="text-right px-5 py-2.5 text-xs font-semibold text-gray-600">Conversions</th>
            <th className="text-right px-5 py-2.5 text-xs font-semibold text-gray-600">Revenue</th>
            <th className="text-right px-5 py-2.5 text-xs font-semibold text-gray-600">Spend</th>
          </tr></thead>
          <tbody className="divide-y divide-gray-50">
            {channelSummary.map(ch => (
              <tr key={ch.channel} className="hover:bg-gray-50">
                <td className="px-5 py-3 text-sm font-medium text-gray-900 capitalize">{ch.channel.replace('_', ' ')}</td>
                <td className="px-5 py-3 text-sm text-right text-gray-700">{ch.impressions.toLocaleString()}</td>
                <td className="px-5 py-3 text-sm text-right text-gray-700">{ch.clicks.toLocaleString()}</td>
                <td className="px-5 py-3 text-sm text-right text-gray-700">{ch.leads}</td>
                <td className="px-5 py-3 text-sm text-right text-gray-700">{ch.conversions}</td>
                <td className="px-5 py-3 text-sm text-right text-green-700 font-medium">₹{(ch.revenue / 1000).toFixed(0)}K</td>
                <td className="px-5 py-3 text-sm text-right text-red-600">₹{(ch.spend / 1000).toFixed(0)}K</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Company Comparison */}
      {companySummary.length > 0 && (
        <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
          <div className="px-5 py-3 bg-gray-50 border-b border-gray-200"><h3 className="text-sm font-bold text-gray-900">Company Comparison</h3></div>
          <div className="p-5 grid grid-cols-1 md:grid-cols-3 gap-3">
            {companySummary.map(cs => (
              <div key={cs.company} className="p-3 bg-gray-50 rounded-lg">
                <div className="text-sm font-medium text-gray-900">{cs.company}</div>
                <div className="flex justify-between mt-2 text-xs">
                  <span className="text-gray-500">Leads: <span className="font-medium text-gray-800">{cs.leads}</span></span>
                  <span className="text-gray-500">Revenue: <span className="font-medium text-green-700">₹{(cs.revenue / 1000).toFixed(0)}K</span></span>
                  <span className="text-gray-500">Spend: <span className="font-medium text-red-600">₹{(cs.spend / 1000).toFixed(0)}K</span></span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Campaign Performance */}
      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
        <div className="px-5 py-3 bg-gray-50 border-b border-gray-200"><h3 className="text-sm font-bold text-gray-900">Campaign Performance</h3></div>
        <div className="p-5 space-y-3">
          {campaigns.map(c => {
            const co = companies.find(comp => comp.id === c.companyId);
            return (
              <div key={c.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div>
                  <div className="text-sm font-medium text-gray-900">{c.name}</div>
                  <div className="text-xs text-gray-500">🏢 {co?.name} · {c.channels.join(', ')}</div>
                </div>
                <div className="flex gap-4 text-xs">
                  <div className="text-center"><div className="text-gray-400">Leads</div><div className="font-bold text-gray-900">{c.leads}</div></div>
                  <div className="text-center"><div className="text-gray-400">Conv</div><div className="font-bold text-gray-900">{c.conversions}</div></div>
                  <div className="text-center"><div className="text-gray-400">Revenue</div><div className="font-bold text-green-700">₹{(c.revenue / 1000).toFixed(0)}K</div></div>
                  <div className="text-center"><div className="text-gray-400">ROI</div><div className="font-bold text-gray-900">{c.roi > 0 ? `${c.roi}x` : '—'}</div></div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
